import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  Container,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  alpha,
  TextField
} from '@mui/material';
import { motion } from 'framer-motion';
import { styled, useTheme } from '@mui/material/styles';
import { bg, text as textColors, border, shadow } from '../../utils/darkModeAdapter';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import MicIcon from '@mui/icons-material/Mic';
import ScienceIcon from '@mui/icons-material/Science';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import { showError } from '../../utils/errorHandler';
import { useAuth } from '../../hooks/useAuth';
import axios from 'axios';
import { selectionCard, selectionCardButton, designTokens } from '../../theme/designTokens';
import { TemplateSelector, TemplateEditor } from './components/Templates';

const theme = designTokens.featureThemes.interview_support;

const SelectionCard = styled(Paper)(({ theme: muiTheme }) => ({
  ...selectionCard('interview_support', muiTheme),
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
}));

const ActionButton = styled(Button)(({ theme: muiTheme }) => ({
  ...selectionCardButton('interview_support'),
}));

const getStatusColor = (status) => {
  switch (status) {
    case 'evaluated': return 'success';
    case 'completed': return 'info';
    case 'in_progress': return 'warning';
    case 'draft': return 'default';
    case 'archived': return 'default';
    default: return 'default';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'evaluated': return '評価済み';
    case 'completed': return '面接完了';
    case 'in_progress': return '進行中';
    case 'draft': return '下書き';
    case 'archived': return 'アーカイブ';
    default: return '不明';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'evaluated': return <CheckCircleIcon />;
    case 'completed': return <CheckCircleIcon />;
    case 'in_progress': return <PendingIcon />;
    case 'draft': return <AssignmentIcon />;
    case 'archived': return <AssignmentIcon />;
    default: return <AssignmentIcon />;
  }
};

function SelectionScreen({ onCreateNew, onSelectExisting, onAudioOnlyStart, loading = false }) {
  const muiTheme = useTheme();
  const { user } = useAuth(); // 現在のユーザー情報を取得
  const [checkSheets, setCheckSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [error, setError] = useState(null);

  // メニュー管理用のstate
  const [anchorEl, setAnchorEl] = useState(null);
  const [historyDialog, setHistoryDialog] = useState({ open: false, checkSheets: [] });
  const [startFromPastDialog, setStartFromPastDialog] = useState({ open: false, checkSheets: [] });

  // テンプレート選択ダイアログ用のstate
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [useTemplate, setUseTemplate] = useState(true);

  // テンプレート管理用のstate
  const [templateManagementOpen, setTemplateManagementOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(undefined); // undefined=一覧表示, null=新規作成, object=編集
  const [isEditing, setIsEditing] = useState(false);

  // 子アカウントかどうかを判定
  const isInterviewOnlyUser = user?.role === 'interview_only';

  // 子アカウント専用：未完了チェックシートを取得する関数
  const fetchIncompleteCheckSheets = async () => {
    try {
      setLoadingSheets(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/incomplete-check-sheets`,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      const data = response.data;
      setCheckSheets(data);
      return data;
    } catch (error) {
      console.error('Error fetching incomplete check sheets:', error);
      if (error.response?.status === 403) {
        setError('このアカウントでは面接チェックシートにアクセスできません。管理者にお問い合わせください。');
      } else {
        setError('チェックシートの取得に失敗しました。しばらく時間をおいて再度お試しください。');
      }
      return [];
    } finally {
      setLoadingSheets(false);
    }
  };

  // 過去のチェックシートを取得する関数（親アカウント用 - 既存機能）
  const fetchCheckSheets = async () => {
    try {
      setLoadingSheets(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets`,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      const data = response.data;
      setCheckSheets(data);
      return data;
    } catch (error) {
      console.error('Error fetching check sheets:', error);
      setError(error.message);
      return [];
    } finally {
      setLoadingSheets(false);
    }
  };

  // メニューを開く
  const handleMenuOpen = async (event) => {
    setAnchorEl(event.currentTarget);
  };

  // メニューを閉じる
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // 過去の面接結果を確認
  const handleViewHistory = async () => {
    handleMenuClose();
    const data = await fetchCheckSheets();
    setHistoryDialog({ open: true, checkSheets: data });
  };

  // 過去の面接から開始
  const handleStartFromPast = async () => {
    handleMenuClose();
    const data = await fetchCheckSheets();
    setStartFromPastDialog({ open: true, checkSheets: data });
  };

  // 新しい面接を開始（テンプレート選択ダイアログを開く）
  const handleNewInterviewClick = () => {
    setTemplateDialogOpen(true);
  };

  // テンプレート選択後に面接を開始
  const handleStartWithTemplate = () => {
    setTemplateDialogOpen(false);
    // テンプレート情報を含めてonCreateNewを呼び出す
    onCreateNew(useTemplate ? selectedTemplate : null);
    // 状態をリセット
    setSelectedTemplate(null);
    setUseTemplate(true);
  };

  // テンプレートなしで開始
  const handleStartWithoutTemplate = () => {
    setTemplateDialogOpen(false);
    onCreateNew(null);
    setSelectedTemplate(null);
    setUseTemplate(true);
  };

  // 子アカウント用：コンポーネントマウント時に未完了チェックシートを取得
  useEffect(() => {
    if (isInterviewOnlyUser) {
      fetchIncompleteCheckSheets();
    }
  }, [isInterviewOnlyUser]);

  // Card animation
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      },
    },
  };

  // 子アカウント専用の表示（親アカウントには一切影響しない）
  if (isInterviewOnlyUser) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          sx={{ textAlign: 'center', mb: 6 }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontWeight: 700,
              color: theme.primary,
            }}
          >
            面接チェックシート
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            進行中の面接チェックシートから選択してください
          </Typography>
        </Box>

        {loadingSheets && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={48} sx={{ color: theme.primary }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            {error}
          </Alert>
        )}

        {!loadingSheets && !error && checkSheets.length === 0 && (
          <Alert severity="info" sx={{ textAlign: 'center', py: 4, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              利用可能な面接チェックシートがありません
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理者が面接チェッ���シートを作成するまでお待ちください。
            </Typography>
          </Alert>
        )}

        {!loadingSheets && !error && checkSheets.length > 0 && (
          <>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              {checkSheets.length}件の進行中面接があります
            </Typography>
            <Grid container spacing={4}>
              {checkSheets.map((sheet) => (
                <Grid item xs={12} sm={6} md={4} key={sheet.id}>
                  <Card
                    component={motion.div}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '2px solid transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 8px 24px ${theme.shadowHover}`,
                        borderColor: theme.primary,
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => onSelectExisting(sheet)}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AssignmentIcon sx={{ color: theme.primary, fontSize: 24 }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                          {sheet.position || '職種未設定'}
                        </Typography>
                        <Chip
                          label={getStatusLabel(sheet.status)}
                          color={getStatusColor(sheet.status)}
                          size="small"
                        />
                      </Box>

                      {sheet.candidate_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          候補者: {sheet.candidate_name}
                        </Typography>
                      )}

                      {sheet.interview_stage && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          段階: {sheet.interview_stage}
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        作成日: {new Date(sheet.created_at).toLocaleDateString()}
                      </Typography>

                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 3
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectExisting(sheet);
                          }}
                        >
                          面接を開始
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* リフレッシュボタン */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchIncompleteCheckSheets}
                sx={{ borderRadius: 2 }}
              >
                更新
              </Button>
            </Box>
          </>
        )}
      </Container>
    );
  }

  // ★★★ 親アカウント（管理者・一般）用の既存表示は一切変更しない ★★★
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        sx={{ textAlign: 'center', mb: 6 }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: theme.primary,
          }}
        >
          面接サポートを開始
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          新しい面接を開始するか、過去の面接から選択してください
        </Typography>
      </Box>

      {/* アクション選択カード */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        {/* 新規作成 */}
        <Grid item xs={12} md={4}>
          <SelectionCard
            component={motion.div}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ y: -8 }}
            onClick={handleNewInterviewClick}
          >
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
              {/* Icon */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '12px',
                    backgroundColor: `${theme.primary}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AddIcon sx={{ fontSize: 32, color: theme.primary }} />
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: textColors(muiTheme).primary,
                    mb: 2,
                  }}
                >
                  新しい面接を開始
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  職種情報を入力して新しい面接チェックシートを作成します
                </Typography>
              </Box>

              {/* Action Button */}
              <ActionButton
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNewInterviewClick();
                }}
              >
                新しい面接を始める
              </ActionButton>
            </Box>
          </SelectionCard>
        </Grid>

        {/* 過去から選択 */}
        <Grid item xs={12} md={4}>
          <SelectionCard
            component={motion.div}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ y: -8 }}
            onClick={handleMenuOpen}
          >
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
              {/* Icon */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '12px',
                    backgroundColor: `${theme.primary}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 32, color: theme.primary }} />
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: textColors(muiTheme).primary,
                    mb: 2,
                  }}
                >
                  過去の面接から選択
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  過去の面接結果を確認するか、続きから開始できます
                </Typography>
              </Box>

              {/* Action Button */}
              <ActionButton
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
              >
                過去の面接を選択
              </ActionButton>
            </Box>
          </SelectionCard>
        </Grid>

        {/* 音声解析のみ */}
        <Grid item xs={12} md={4}>
          <SelectionCard
            component={motion.div}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ y: -8 }}
            onClick={onAudioOnlyStart}
          >
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
              {/* Icon */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '12px',
                    backgroundColor: `${theme.primary}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MicIcon sx={{ fontSize: 32, color: theme.primary }} />
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: textColors(muiTheme).primary,
                    mb: 2,
                  }}
                >
                  音声解析のみ
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  チェックシート生成をスキップして音声解析機能のみを使用します
                </Typography>
              </Box>

              {/* Action Button */}
              <ActionButton
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  onAudioOnlyStart();
                }}
              >
                音声解析を開始
              </ActionButton>
            </Box>
          </SelectionCard>
        </Grid>
      </Grid>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          {error}
        </Alert>
      )}

      {/* ローディング表示 */}
      {loadingSheets && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} sx={{ color: theme.primary }} />
        </Box>
      )}

      {/* 過去の面接選択メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 250,
            boxShadow: shadow(muiTheme).lg
          }
        }}
      >
        <MenuItem
          onClick={handleViewHistory}
          sx={{ py: 1.5, px: 2 }}
        >
          <ListItemIcon>
            <HistoryIcon sx={{ color: theme.primary }} />
          </ListItemIcon>
          <ListItemText>
            過去の面接結果を確認
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleStartFromPast}
          sx={{ py: 1.5, px: 2 }}
        >
          <ListItemIcon>
            <PlayArrowIcon sx={{ color: theme.primary }} />
          </ListItemIcon>
          <ListItemText>
            過去の面接から開始
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* 過去の面接結果確認ダイアログ */}
      <Dialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, checkSheets: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: shadow(muiTheme).lg
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.light} 100%)`,
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <HistoryIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              過去の面接結果
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {historyDialog.checkSheets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                過去の面接履歴がありません
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                面接をクリックまたは「開く」ボタンで選択した面接を開始できます
              </Typography>
              <List>
                {historyDialog.checkSheets.map((sheet, index) => (
                  <React.Fragment key={sheet.id}>
                    <ListItem
                      button
                      onClick={() => {
                        onSelectExisting(sheet);
                        setHistoryDialog({ open: false, checkSheets: [] });
                      }}
                      sx={{
                        py: 2,
                        borderRadius: 2,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: `${theme.primary}15`
                        }
                      }}
                    >
                      <ListItemIcon>
                        {getStatusIcon(sheet.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {sheet.position || '職種未設定'}
                            </Typography>
                            <Chip
                              label={getStatusLabel(sheet.status)}
                              color={getStatusColor(sheet.status)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            {sheet.candidate_name && (
                              <Typography variant="body2" color="text.secondary">
                                候補者: {sheet.candidate_name}
                              </Typography>
                            )}
                            {sheet.interview_stage && (
                              <Typography variant="body2" color="text.secondary">
                                段階: {sheet.interview_stage}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              作成日: {new Date(sheet.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box sx={{ ml: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 2
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // ListItemのclickイベントを防ぐ
                            onSelectExisting(sheet);
                            setHistoryDialog({ open: false, checkSheets: [] });
                          }}
                        >
                          開く
                        </Button>
                      </Box>
                    </ListItem>
                    {index < historyDialog.checkSheets.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setHistoryDialog({ open: false, checkSheets: [] })}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 過去の面接から開始ダイアログ */}
      <Dialog
        open={startFromPastDialog.open}
        onClose={() => setStartFromPastDialog({ open: false, checkSheets: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: shadow(muiTheme).lg
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.light} 100%)`,
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <PlayArrowIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              過去の面接から開始
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {startFromPastDialog.checkSheets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                過去の面接履歴がありません
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                開始したい面接を選択してください
              </Typography>
              <List>
                {startFromPastDialog.checkSheets.map((sheet, index) => (
                  <React.Fragment key={sheet.id}>
                    <ListItem
                      button
                      onClick={() => {
                        onSelectExisting(sheet);
                        setStartFromPastDialog({ open: false, checkSheets: [] });
                      }}
                      sx={{
                        py: 2,
                        borderRadius: 2,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: `${theme.primary}15`
                        }
                      }}
                    >
                      <ListItemIcon>
                        {getStatusIcon(sheet.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {sheet.position || '職種未設定'}
                            </Typography>
                            <Chip
                              label={getStatusLabel(sheet.status)}
                              color={getStatusColor(sheet.status)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            {sheet.candidate_name && (
                              <Typography variant="body2" color="text.secondary">
                                候補者: {sheet.candidate_name}
                              </Typography>
                            )}
                            {sheet.interview_stage && (
                              <Typography variant="body2" color="text.secondary">
                                段階: {sheet.interview_stage}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              作成日: {new Date(sheet.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < startFromPastDialog.checkSheets.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setStartFromPastDialog({ open: false, checkSheets: [] })}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* テンプレート選択ダイアログ */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: shadow(muiTheme).lg,
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            py: 2.5,
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.light} 100%)`,
            color: 'white',
            textAlign: 'center',
            fontWeight: 'bold'
          }}
        >
          面接の開始方法を選択
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* 選択カード */}
          <Grid container sx={{ minHeight: 280 }}>
            {/* テンプレートを使用 */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                borderRight: { md: `1px solid ${border(muiTheme).default}` },
                borderBottom: { xs: `1px solid ${border(muiTheme).default}`, md: 'none' }
              }}
            >
              <Box
                onClick={() => {
                  setUseTemplate(true);
                  setSelectedTemplate(null);
                }}
                sx={{
                  p: 4,
                  height: '100%',
                  cursor: 'pointer',
                  backgroundColor: useTemplate ? `${theme.primary}08` : 'transparent',

                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: `${theme.primary}05`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: `${theme.primary}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <DescriptionIcon sx={{ color: theme.primary, fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: textColors(muiTheme).primary }}>
                      テンプレートを使用
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      推奨
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  科学的根拠に基づいた評価基準（BARS）で、一貫性のある公平な面接評価が可能になります。
                </Typography>
                {useTemplate && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="選択中"
                    size="small"
                    sx={{ mt: 2, backgroundColor: theme.primary, color: '#fff' }}
                  />
                )}
              </Box>
            </Grid>

            {/* テンプレートなし */}
            <Grid item xs={12} md={6}>
              <Box
                onClick={() => {
                  setUseTemplate(false);
                  setSelectedTemplate(null);
                }}
                sx={{
                  p: 4,
                  height: '100%',
                  cursor: 'pointer',
                  backgroundColor: !useTemplate ? `${theme.primary}08` : 'transparent',

                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: `${theme.primary}05`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: bg(muiTheme).tinted,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AddIcon sx={{ color: textColors(muiTheme).muted, fontSize: 28 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: textColors(muiTheme).primary }}>
                    テンプレートなし
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  AIが職種に応じたチェックシートを自動生成します。カスタマイズ性が高い方法です。
                </Typography>
                {!useTemplate && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="選択中"
                    size="small"
                    sx={{ mt: 2, backgroundColor: theme.primary, color: '#fff' }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          {/* テンプレート選択エリア */}
          {useTemplate && (
            <Box sx={{ borderTop: `1px solid ${border(muiTheme).default}`, p: 3, backgroundColor: bg(muiTheme).subtle }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(muiTheme).secondary }}>
                  テンプレートを選択してください
                </Typography>
                <Button
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={() => {
                    setTemplateDialogOpen(false);
                    setTemplateManagementOpen(true);
                  }}
                  sx={{ color: theme.primary, textTransform: 'none' }}
                >
                  テンプレート管理
                </Button>
              </Box>
              <Box sx={{ maxHeight: '35vh', overflow: 'auto', backgroundColor: bg(muiTheme).card, borderRadius: 2, p: 2 }}>
                <TemplateSelector
                  onSelectTemplate={setSelectedTemplate}
                  selectedTemplateId={selectedTemplate?.id}
                  showActions={false}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${border(muiTheme).default}`, backgroundColor: bg(muiTheme).subtle }}>
          <Button
            onClick={() => setTemplateDialogOpen(false)}
            sx={{ borderRadius: 2, color: textColors(muiTheme).muted }}
          >
            キャンセル
          </Button>
          <Button
            onClick={useTemplate ? handleStartWithTemplate : handleStartWithoutTemplate}
            variant="contained"
            disabled={useTemplate && !selectedTemplate}
            startIcon={<PlayArrowIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.2,
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
          >
            次へ進む
          </Button>
        </DialogActions>
      </Dialog>

      {/* テンプレート管理ダイアログ */}
      <Dialog
        open={templateManagementOpen}
        onClose={() => {
          setTemplateManagementOpen(false);
          setEditingTemplate(undefined);
          setIsEditing(false);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: shadow(muiTheme).lg,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            py: 2.5,
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.light} 100%)`,
            color: 'white',
            textAlign: 'center',
            fontWeight: 'bold'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <SettingsIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              テンプレート管理
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {isEditing ? (
            // テンプレート編集画面
            <TemplateEditor
              template={editingTemplate}
              onSave={() => {
                setIsEditing(false);
                setEditingTemplate(undefined);
              }}
              onCancel={() => {
                setIsEditing(false);
                setEditingTemplate(undefined);
              }}
              embedded={true}
            />
          ) : (
            // テンプレート一覧
            <TemplateSelector
              onSelectTemplate={(template) => {
                // 選択時は何もしない（編集ボタンから編集）
              }}
              onEditTemplate={(template) => {
                setEditingTemplate(template);
                setIsEditing(true);
              }}
              showActions={true}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${border(muiTheme).default}` }}>
          <Button
            onClick={() => {
              setTemplateManagementOpen(false);
              setEditingTemplate(undefined);
              setIsEditing(false);
            }}
            sx={{ borderRadius: 2, color: textColors(muiTheme).muted }}
          >
            閉じる
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setTemplateManagementOpen(false);
              setEditingTemplate(undefined);
              setIsEditing(false);
              setTemplateDialogOpen(true);
            }}
            sx={{ borderRadius: 2 }}
          >
            面接開始に戻る
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SelectionScreen;
